import { Button, Drawer, Skeleton, Typography } from "@mui/material";
import { CoreExpandIcon, CorePaper, CoreStepper, StepConfig, VerityColorLogo } from "../../core";
import i18nPackage from "../../i18n/i18n";
import styles from "./EditQuizStepper.module.css";
import { EditQuizStep } from "../proctoring-options";
import { useId, useMemo, useState } from "react";
import { ConditionalWrapper } from "../../wrapper";
import { useMobileLayout } from "../../hooks";

type MobileStepDrawerProps = {
	children: React.ReactNode;
	stepNumber?: number;
	buttonText?: string;
	expanded: boolean;
	onOpen: () => void;
	onClose: () => void;
};

function MobileStepDrawer({ children, buttonText, expanded, onClose, onOpen, stepNumber }: MobileStepDrawerProps) {
	const controlId = useId();
	const ariaLabelledBy = useId();

	return (
		<>
			<ConditionalWrapper
				condition={buttonText === undefined}
				whenTrue={(children) => (
					<Skeleton variant="rounded" width={"100%"}>
						{children}
					</Skeleton>
				)}
			>
				<Button
					className={styles.mobileStepButton}
					onClick={onOpen}
					aria-expanded={expanded}
					aria-controls={controlId}
					id={ariaLabelledBy}
				>
					{stepNumber && <span className={styles.mobileStepNumber}>{stepNumber}</span>}
					<span>
						{buttonText}
						<CoreExpandIcon expanded={expanded} variant="narrow" />
					</span>
				</Button>
			</ConditionalWrapper>
			<Drawer
				id={controlId}
				anchor="top"
				open={expanded}
				onClose={onClose}
				PaperProps={{
					role: "dialog",
					"aria-labelledby": ariaLabelledBy,
				}}
			>
				{children}
			</Drawer>
		</>
	);
}

type EditQuizStepperProps = {
	className?: {
		wrapper?: string;
		title?: string;
		stepper?: string;
	};
	activeStep: number;
	steps: StepConfig<EditQuizStep>[];
	onStepChange: (step: number) => void;
	completedSteps: number[];
	loading?: boolean;
};

export default function EditQuizStepper({
	activeStep,
	completedSteps,
	onStepChange,
	steps,
	className,
	loading,
}: EditQuizStepperProps) {
	const [expanded, setExpanded] = useState<boolean>(false);
	const titleProps = className?.title;
	const stepperProps = className?.stepper;

	const isMobileLayout = useMobileLayout();

	const currentStepNumber = useMemo(() => {
		if (!steps || steps.length === 0) return 0;

		const currentStep = steps[activeStep];
		if (!currentStep) return 0;

		// Filter to get only the primary (parent) steps
		const parentSteps = steps.filter((s) => !s.parentSubstep);

		// Find the key of the parent for the active step
		const targetKey = currentStep.parentSubstep || currentStep.key;

		// Return the 1-based index
		return parentSteps.findIndex((s) => s.key === targetKey) + 1;
	}, [activeStep, steps]);

	const activeStepName = useMemo(() => {
		if (loading) return undefined;
		return steps[activeStep]?.label;
	}, [activeStep, loading, steps]);

	const stepperWrapperClassName = `${styles.stepperWrapper} ${className?.wrapper || ""}`;

	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper component={"section"} className={stepperWrapperClassName}>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => <div className={stepperWrapperClassName}>{children}</div>}
		>
			<div className={`${styles.editQuizTitleRow} ${titleProps || ""}`}>
				<VerityColorLogo width={30} height={30} />
				<Typography variant="h2">{i18nPackage.t("editQuizPage.setupProctoring")}</Typography>
			</div>
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => (
					<MobileStepDrawer
						expanded={expanded}
						onClose={() => setExpanded(false)}
						onOpen={() => setExpanded(true)}
						buttonText={activeStepName}
						stepNumber={currentStepNumber}
					>
						{children}
					</MobileStepDrawer>
				)}
			>
				<CoreStepper
					className={`${styles.headerStepper} ${stepperProps || ""}`}
					activeStep={activeStep}
					steps={steps}
					onStepChange={(step) => {
						onStepChange(step);
						setExpanded(false);
					}}
					completedSteps={completedSteps}
					vertical={true}
					loading={loading}
				/>
			</ConditionalWrapper>
		</ConditionalWrapper>
	);
}
